import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        navy:
          "rounded-full bg-brand-navy text-white font-semibold border border-white/10 hover:-translate-y-px hover:bg-brand-navy-light transition-all",
        accent:
          "rounded-full bg-gradient-to-br from-brand-accent-soft via-brand-accent-bright via-60% to-brand-accent text-brand-navy font-bold tracking-[0.02em] border border-white/35 shadow-[0_8px_24px_-6px_rgba(141,198,63,0.55),inset_0_1px_0_rgba(255,255,255,0.4)] hover:-translate-y-px hover:brightness-105 hover:shadow-[0_12px_28px_-6px_rgba(141,198,63,0.65),inset_0_1px_0_rgba(255,255,255,0.5)] active:translate-y-0 transition-all",
        "outline-navy":
          "rounded-full border-[1.5px] border-brand-navy bg-transparent text-brand-navy font-semibold hover:bg-brand-navy hover:text-white transition-all",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    compoundVariants: [
      { variant: "navy", size: "default", class: "h-11 rounded-full px-[22px] text-sm" },
      { variant: "accent", size: "default", class: "h-11 rounded-full px-[22px] text-sm" },
      { variant: "outline-navy", size: "default", class: "h-11 rounded-full px-[22px] text-sm" },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
